/** @format */

import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import Order from '../../interfaces/forms/Order';
import Filter from '../../interfaces/forms/Filter';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import PaperedTable from '../../components/Table/PaperedTable';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import { useDispatch, useSelector } from 'react-redux';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import Pager from '../../components/Adescom/PagerRange';
import { makeStyles, TableFooter, withStyles } from '@material-ui/core';
import {
    platformUserDefinedRolesToGroupsConstants,
    platformUserDefinedRolesToGroupsReducers,
    platformUserDefinedRolesToGroupsType,
} from '../../modules/platformGroups/reducers';
import { UserPermissionGroupListTable } from './UserPermissionGroupListTable';
import FabAddButton from '../../components/Buttons/FabAddButton';
import { history } from '../../core/history';
import AlertInfo from '../../components/Info/AlertInfo';
import { store } from '../../core/store';
import PlatformUserDefinedRolesInterface from '../../interfaces/platform/PlatformUserDefinedRolesInterface';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';

store.injectReducer('platformUserDefinedRolesToGroupsReducers', platformUserDefinedRolesToGroupsReducers);

export const UserPermissionGroupList = (props: { classes: any }): React.ReactElement => {
    const dispatch = useDispatch();

    const [activeRow, setActiveRow] = useState<number>(0);
    const [filters, setFilters] = useState<Array<Filter>>([{ name: 'superUserRole', value: false }]);
    const [orders, setOrders] = useState<Array<Order>>([{ name: 'mainname', value: 'asc' }]);
    const [alert, setAlert] = useState<ReactNode | null>(null);

    const USER_PERMISSION_GROUP_URI = '/platform_user_defined_roles';

    const { collections } = useSelector((state: { platformUserDefinedRolesToGroupsReducers: platformUserDefinedRolesToGroupsType }) => {
        return state.platformUserDefinedRolesToGroupsReducers;
    });

    const setActiveRowCallback = (id: number): void => {
        if (activeRow === id) {
            id = 0;
        }
        setActiveRow(id);
    };

    const clearFilter = (): void => {
        setFilters([]);
        setOrders([]);
        firstLoad();
    };

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);

    const firstLoad = useCallback(
        (uri?: string) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(USER_PERMISSION_GROUP_URI);
            }
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                platformUserDefinedRolesToGroupsConstants.LIST,
                filters,
                orders,
                (data: PlatformUserDefinedRolesInterface) => {},
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: true,
                }
            );
        },
        [dispatch, filters, orders]
    );

    const paginationCallbackFunction = useCallback(
        (uri?: string) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(USER_PERMISSION_GROUP_URI);
            }
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                platformUserDefinedRolesToGroupsConstants.LIST,
                filters,
                orders,
                (data: PlatformUserDefinedRolesInterface) => {},
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: true,
                }
            );
        },
        [dispatch, filters, orders]
    );

    const addCallback = (): void => {
        const redirectUri = FormGeneralHelper.getRouteUrl('/user-permission-group-add');
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.push(redirectUri);
    };

    const editCallback = (data: { id: number }): void => {
        const redirectUri = FormGeneralHelper.getRouteUrl('/user-permission-group-edit/' + data.id);
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.push(redirectUri);
    };

    const deleteCallback = (data: { id: number }): void => {
        setAlert(
            <AlertInfo
                onConfirm={(): void => {
                    deleteConfirmed({ id: data.id });
                }}
                onCancel={deleteCancelled}
                confirmBtnText={'general:common.delete'}
                cancelBtnText={'general:common.cancel'}
                content="permission_groups:a.confirm_delete_permission_group"
            />
        );
    };

    const deleteCancelled = (): void => {
        setAlert(null);
    };

    const deleteConfirmed = (data: { id: number }): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        this.itemLoaders.deleteCall(
            FormGeneralHelper.getByUri('/user_permission_group_edit/ ' + data.id),
            platformUserDefinedRolesToGroupsConstants.DELETE,
            () => {
                setAlert(null);
                firstLoad();
            },
            { dispatchAfterCallback: false, dispatchErrorAfterCallback: true }
        );
    };

    useEffect(() => {
        firstLoad();
    }, [firstLoad]);

    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable>
                    {collections && (
                        <UserPermissionGroupListTable
                            collectionsItems={collections['hydra:member']}
                            editCallback={editCallback}
                            deleteCallback={deleteCallback}
                            classes={props.classes}
                            setActiveRow={setActiveRowCallback}
                            activeRow={activeRow}
                            firstLoad={firstLoad}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            filters={filters}
                            orders={orders}
                        />
                    )}
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={10}>
                                {collections && collections['hydra:view'] && <Pager callback={paginationCallbackFunction} views={collections} />}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
            </GridItem>
            <FabAddButton callback={addCallback} />
            {alert}
        </GridContainer>
    );
};

export default withStyles(oplAppStyle)(UserPermissionGroupList);
